import { CONFIG } from '../config.js';
import { viewSurvey } from '../api/backend.js';
import { getBrowserType, getDeviceType } from '../utils/browser.js';
import { getStoredUTMParameters } from '../utils/utm.js';

// The backend tracks every participation as a session. The widget only
// remembers the id the server handed out and echoes it on follow-up requests
// of the same survey; the server stays the authority on visibility.

const SESSION_KEY_PREFIX = 'ph_survey_session_';

// Losing one impression is preferable to showing a survey against its
// visibility rule, so a slow view request simply means "do not render".
export const SURVEY_VIEW_TIMEOUT = 10000;

export function normalizeSessionId(value) {
    if (value === null || value === undefined || value === '') return null;
    const id = Number(value);
    return Number.isInteger(id) ? id : null;
}

function sessionKey(surveyId) {
    return `${SESSION_KEY_PREFIX}${surveyId}`;
}

// sessionStorage keyed by survey uuid: never stored in the ph_user_id cookie
export function getStoredSessionId(surveyId) {
    try {
        return normalizeSessionId(sessionStorage.getItem(sessionKey(surveyId)));
    } catch {
        return null;
    }
}

export function storeSessionId(surveyId, sessionId) {
    const id = normalizeSessionId(sessionId);
    try {
        if (id === null) {
            sessionStorage.removeItem(sessionKey(surveyId));
        } else {
            sessionStorage.setItem(sessionKey(surveyId), String(id));
        }
    } catch {
        // sessionStorage unavailable (e.g. blocked site data): keep it in memory only
    }
}

export function clearStoredSessionId(surveyId) {
    storeSessionId(surveyId, null);
}

function addToStoredList(storage, key, surveyId) {
    if (!storage) return;
    let list = storage.get(key) ?? [];
    if (!Array.isArray(list)) list = [];
    if (!list.includes(surveyId)) {
        list.push(surveyId);
        storage.set(key, list);
    }
}

export function markSurveyViewed(storage, surveyId) {
    addToStoredList(storage, 'ph_survey_viewed', surveyId);
}

// The local lists are only a shortcut that saves the next view request; the
// server's answer is what actually decides.
export function markSurveyNotDisplayable(storage, surveyId, visibility) {
    switch (visibility) {
        case 'once':
            addToStoredList(storage, 'ph_survey_viewed', surveyId);
            break;
        case 'atLeastOneAnswer':
            addToStoredList(storage, 'ph_survey_at_least_one_answer', surveyId);
            break;
        case 'untilSubmitted':
            addToStoredList(storage, 'ph_survey_completed', surveyId);
            break;
        default:
            // 'always' (or unknown): nothing to cache, ask again next time
            break;
    }
}

export function buildSurveyViewPayload(userId, storage, sessionId = null) {
    const payload = {
        user_id: userId,
        url_user: window.location.href,
        browser_name: getBrowserType(),
        device_type: getDeviceType(),
        country_name: new Intl.DateTimeFormat().resolvedOptions().locale || 'unknown',
        ...getStoredUTMParameters(storage),
    };
    const id = normalizeSessionId(sessionId);
    if (id !== null) payload.session_id = id;
    return payload;
}

async function readJsonBody(response) {
    try {
        const text = await response.text();
        if (!text) return null;
        return JSON.parse(text);
    } catch {
        return null;
    }
}

/**
 * Asks the server whether a survey may be shown to this user right now.
 * Must be awaited before anything of the survey is rendered.
 *
 * Resolves to { display, sessionId, visibility }. `display` is false on any
 * error or timeout; the local cache is only updated on a definite answer.
 */
export async function requestSurveyDisplay(survey, storage, userId) {
    const surveyId = survey.survey_id;
    const settingsVisibility = survey.survey_settings?.visibility || 'always';
    const previousSessionId = getStoredSessionId(surveyId);

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), SURVEY_VIEW_TIMEOUT) : null;

    let response;
    try {
        response = await viewSurvey(
            CONFIG.apiUrl,
            surveyId,
            buildSurveyViewPayload(userId, storage, previousSessionId),
            controller ? { signal: controller.signal } : {},
        );
    } catch {
        return { display: false, sessionId: null, visibility: settingsVisibility, error: true };
    } finally {
        if (timer) clearTimeout(timer);
    }

    if (!response || !response.ok) {
        return { display: false, sessionId: null, visibility: settingsVisibility, error: true };
    }

    const data = await readJsonBody(response);
    const visibility = typeof data?.visibility === 'string' ? data.visibility : settingsVisibility;

    if (typeof data?.display !== 'boolean') {
        // A backend without the session model answers 204 without a body. It
        // accepted the view, so keep the legacy behaviour: render, no session.
        markSurveyViewed(storage, surveyId);
        clearStoredSessionId(surveyId);
        return { display: true, sessionId: null, visibility, legacy: true };
    }

    if (!data.display) {
        markSurveyNotDisplayable(storage, surveyId, visibility);
        clearStoredSessionId(surveyId);
        return { display: false, sessionId: null, visibility };
    }

    // Always trust the id from the latest server answer, never the remembered one
    const sessionId = normalizeSessionId(data.session_id);
    storeSessionId(surveyId, sessionId);
    markSurveyViewed(storage, surveyId);
    return { display: true, sessionId, visibility };
}
